import type { ComponentWithValues } from './ComponentWithValuesDocuments';
import type { StackConfig } from './StackConfigDocuments';
import {
  listComponentEnvConfigPaths,
  listComponentEnvironmentVariables,
  type ListComponentEnvironmentVariablesParams,
} from './stackEnvUtils';

const matchWildcard = (a: string, b: string): boolean => {
  if (a.length === 0 && b.length === 0) return true;
  if (a.length > 1 && a[0] === '*' && b.length == 0) return false;

  if (a.length !== 0 && b.length !== 0 && a[0] === b[0]) {
    return matchWildcard(a.substring(1), b.substring(1));
  }

  if (a.length > 0 && a[0] === '*') {
    return matchWildcard(a.substring(1), b) || matchWildcard(a, b.substring(1));
  }

  return false;
};

export interface ImportComponentVariablesParams {
  stackConfig: Pick<StackConfig, 'components' | 'addons'>;
  components: (ListComponentEnvironmentVariablesParams['component'] & { stackComponentID: number })[];
  componentWithValues: Pick<ComponentWithValues, 'identifier'>;
}

/**
 * Process import rules & output the correct imported environment variables for a component.
 */
export const importComponentVariables = ({ stackConfig, components, componentWithValues }: ImportComponentVariablesParams) => {
  const componentStackConfig = [...(stackConfig.components || []), ...(stackConfig.addons || [])].find(
    (component) => component.identifier === componentWithValues.identifier
  );

  // List all available environment variables
  const environmentVariables = components
    .filter((component) => component.identifier !== componentWithValues.identifier)
    .flatMap((component) =>
      Object.entries(
        listComponentEnvironmentVariables({
          component,
          includeUserDefinedVariables: true,
        }).reduce((list, env) => ({ ...list, [env.identifier]: env.value }), {} as { [identifier: string]: string })
      ).map(([identifier, value]) => ({
        stackComponentID: component.stackComponentID,
        identifier: `${component.identifier}.${identifier}`,
        value,
      }))
    );

  const includePaths = listComponentEnvConfigPaths({ include: componentStackConfig?.env?.include }).map((pathWithKey) => {
    const [path, identifier] = pathWithKey.split(':');
    return { path, identifier: identifier || null };
  });

  // FIXME: Handle exclude!

  const importedEnvironmentVariables = environmentVariables
    .map((env) => {
      const match = includePaths.find((match) => matchWildcard(match.path, env.identifier));
      const identifier = env.identifier.split('.').at(-1) as string;
      return {
        stackComponentID: env.stackComponentID,
        identifier: match?.identifier || identifier,
        value: (match ? env.value : null) as string,
      };
    })
    .filter((env) => env.value !== null);

  return importedEnvironmentVariables;
};
